import * as Dialog from '@radix-ui/react-dialog';
import { useRef, useState } from 'react';
import { useStore } from '@/state/store';
import { exportAllToJson, importAll, listQuarantine, restoreLastBackup } from '@/db/backup';
import { fmtCurrency, fmtRatioPct } from '@/lib/format';
import { dateStamp, downloadText, readFileText } from '@/lib/download';

/** Scenario library: save/load/compare/export-import + data health (§10.5, §10.8). */
export function ScenarioLibrary() {
  const scenarios = useStore((s) => s.scenarios);
  const currentId = useStore((s) => s.currentScenarioId);
  const compareSel = useStore((s) => s.ui.comparisonSelection);
  const dirty = useStore((s) => s.dirty);
  const a = useStore();
  const [name, setName] = useState('');
  const [status, setStatus] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const doExport = async () => downloadText(`PAL-scenarios-${dateStamp()}.json`, await exportAllToJson());
  const doImport = async (file: File) => {
    const res = await importAll(await readFileText(file));
    await a.refreshScenarios();
    setStatus(`Imported ${res.imported}, skipped ${res.skipped}.`);
  };
  const dataHealth = async () => {
    const q = await listQuarantine();
    setStatus(`${q.length} quarantined record(s). Use "Restore last backup" if needed.`);
  };

  return (
    <Dialog.Root>
      <Dialog.Trigger className="rounded-md border border-slate-300 px-2 py-1 text-sm hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" data-tour="scenarios">
        Scenarios ({scenarios.length})
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed right-0 top-0 z-50 flex h-full w-[min(92vw,440px)] flex-col border-l border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-900">
          <Dialog.Title className="text-base font-semibold">Scenario library</Dialog.Title>
          <Dialog.Description className="sr-only">Save, load, compare, and back up your scenarios.</Dialog.Description>

          <div className="mt-3 flex gap-2">
            <input
              placeholder="Name to save as…"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-900"
            />
            <button
              type="button"
              disabled={!name.trim()}
              onClick={() => { void a.saveCurrentAs(name.trim()); setName(''); }}
              className="rounded bg-sky-600 px-2 py-1 text-sm text-white disabled:opacity-50"
            >
              Save as
            </button>
          </div>
          {currentId && (
            <button
              type="button"
              onClick={() => void a.updateCurrent()}
              className="mt-2 self-start rounded border border-slate-300 px-2 py-1 text-xs hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800"
            >
              Update current{dirty ? ' (unsaved changes)' : ''}
            </button>
          )}

          <ul className="mt-3 flex-1 overflow-y-auto">
            {scenarios.length === 0 && <li className="text-sm text-slate-500">No saved scenarios yet.</li>}
            {scenarios.map((sc) => (
              <li key={sc.id} className={`mb-2 rounded border p-2 ${sc.id === currentId ? 'border-sky-400' : 'border-slate-200 dark:border-slate-700'}`}>
                <div className="flex items-center justify-between gap-2">
                  <button type="button" className="truncate text-left text-sm font-medium hover:underline" onClick={() => void a.loadScenario(sc.id)}>
                    {sc.name}
                  </button>
                  <label className="flex shrink-0 items-center gap-1 text-xs">
                    <input type="checkbox" className="accent-sky-600" checked={compareSel.includes(sc.id)} onChange={() => void a.toggleCompare(sc.id)} />
                    Compare
                  </label>
                </div>
                <div className="mt-0.5 text-[11px] text-slate-500">
                  {fmtCurrency(sc.inputs.portfolioValue)} · draw {fmtCurrency(sc.inputs.drawAmount)} · adv {fmtRatioPct(sc.inputs.blendedAdvanceRate)}
                </div>
                <div className="mt-1 flex gap-2 text-[11px]">
                  <button type="button" className="text-sky-700 hover:underline dark:text-sky-400" onClick={() => void a.duplicate(sc.id)}>Duplicate</button>
                  <button type="button" className="text-sky-700 hover:underline dark:text-sky-400" onClick={() => { const n = prompt('Rename to', sc.name); if (n) void a.rename(sc.id, n); }}>Rename</button>
                  <button type="button" className="text-danger-surface hover:underline" onClick={() => { if (confirm(`Delete "${sc.name}"?`)) void a.removeScenario(sc.id); }}>Delete</button>
                </div>
              </li>
            ))}
          </ul>

          <div className="mt-2 flex flex-wrap gap-2 border-t border-slate-200 pt-2 text-xs dark:border-slate-700">
            <button type="button" className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" onClick={() => void doExport()}>Export all</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" onClick={() => fileRef.current?.click()}>Import</button>
            <input ref={fileRef} type="file" accept="application/json" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void doImport(f); e.target.value = ''; }} />
            <button type="button" className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" onClick={() => void dataHealth()}>Data health</button>
            <button type="button" className="rounded border border-slate-300 px-2 py-1 hover:bg-slate-100 dark:border-slate-600 dark:hover:bg-slate-800" onClick={() => void restoreLastBackup().then((n) => { void a.refreshScenarios(); setStatus(`Restored ${n} record(s).`); })}>Restore last backup</button>
          </div>
          {status && <p className="mt-1 text-xs text-slate-500">{status}</p>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
